import * as React from "react";
import { useState } from "react";
import {
  Button,
  Modal,
  Steps,
  Form,
  Result,
  Space,
  Col,
  Row,
  Divider,
} from "antd";
import dayjs from "dayjs";

import { useFormContext } from "../../../hooks/useFormContext";
import { UserOutlined, EditOutlined, DeleteOutlined, RightOutlined } from "@ant-design/icons";
import { useStepsForm } from "sunflower-antd";

import PersonalInfo from "../../FormInputs/PersonalInfo";
import AdditionalInfo from "../../FormInputs/AdditionalInfo";
import ResidentialInfo from "../../FormInputs/ResidentialInfo";
import ProductInfo from "../../FormInputs/ProductInfo";
import CoveredLives from "../../FormInputs/CoveredLives";
import Beneficiary from "../../FormInputs/Beneficiary";
import Addon from "../../FormInputs/Addon";
import { AuthContext } from "../../../context/AuthContext";
import { useGuardedModalClose } from "../../../hooks/useGuardedModalClose";
import { toAddonsPayload } from "../../../utils/addonHelpers";
import { calculateAge } from "../../../utils/ageHelper";
import {
  toMemberApiDate,
  toMemberApiInceptionDate,
} from "../../../utils/sastDate";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  member: any;
  showModal: Function;
  handleUpdate: Function;
}

const EditMemberModal: React.FC<IModalProps> = ({
  show,
  member,
  showModal,
  handleUpdate,
}) => {
  const { currentUser } = React.useContext(AuthContext);
  const {
    coveredLive,
    setCoveredLive,
    productInfo,
    setProductInfo,
    coveredProductInfo,
    setCoveredProductInfo,
  } = useFormContext();
  const [cardData, setCardData] = useState<any>(member.CoveredLive || []);
  const [memberAge, setmemberAge] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(0);

  React.useEffect(() => {
    if (member.CoveredLive) {
      setCardData(member.CoveredLive);
    }
  }, [member.CoveredLive]);

  // Populate form fields whenever the modal opens with member data
  React.useEffect(() => {
    if (show && member && member.id) {
      form.setFieldsValue(member);
    }
  }, [show, member]);

  // Calculate member age from DateOfBirth
  React.useEffect(() => {
    if (member.DateOfBirth) {
      const age = calculateAge(member.DateOfBirth);
      if (age != null) {
        setmemberAge(age);
      }
    }
  }, [member.DateOfBirth]);

  const handleAge = (date: Date | string) => {
    const age = calculateAge(date);
    if (age != null) {
      setmemberAge(age);
    }
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    defaultFormValues: member,
    async submit(values: any) {
      setLoading(true);

      const resolveProductTypeName = (value: any): string => {
        if (value == null || value === "") return "";
        if (typeof value === "object") {
          return String(value.label ?? value.value ?? "");
        }
        return String(value);
      };
      const productTypeName = resolveProductTypeName(values.ProductType);
      const extendedAge =
        productTypeName === "One Plus" ? values.ExtendedAge ?? null : null;

      const addons = toAddonsPayload(values.Addons);

      const formattedCardData = cardData.map((item: any) => ({
        ...item,
        ExtendedProduct:
          item.ExtendedProduct && item.ExtendedProduct.value
            ? item.ExtendedProduct.value
            : item.ExtendedProduct,
      }));

      if (member.Beneficiary !== null) {
        const response = await handleUpdate(
          member.id,
          {
            Gender: values.Gender,
            Identity: values.Identity.value,
            IdNumber: values.IdNumber,
            DateOfBirth: toMemberApiDate(values.DateOfBirth),
            Title: values.Title,
            Initials: values.Initials,
            Surname: values.Surname,
            Name: values.Name,
            TelNumber: values.TelNumber,
            Cellphone: values.Cellphone,
            Email: values.Email,
            ExtendedAge: extendedAge,
            InceptionDate: toMemberApiInceptionDate(values.InceptionDate),
            PolicyNumber: values.PolicyNumber && values.PolicyNumber,
            Product: values.Product.value
              ? values.Product.value
              : values.Product,
            Premium: values.Premium ? values.Premium : "",
            SourceOfFunds: values.SourceOfFunds.value
              ? values.SourceOfFunds.value
              : values.SourceOfFunds,
            OtherSourceOfFunds: values.OtherSourceOfFunds,
            AddressLine1: values.AddressLine1,
            AddressLine2: values.AddressLine2,
            Suburb: values.Suburb,
            CityTown: values.CityTown,
            Province: values.Province,
            PostalCode: values.PostalCode,
            CoveredLive: formattedCardData,
            Beneficiary: {
              id: member.Beneficiary.id,
              BenIdNumber: values.BenIdNumber,
              BenDateOfBirth: toMemberApiDate(values.BenDateOfBirth),
              BenTitle: values.BenTitle,
              BenInitials: values.BenInitials,
              BenSurname: values.BenSurname,
              BenName: values.BenName,
              BenGender: values.BenGender,
              BenTelNumber: values.BenTelNumber,
              BenCellphone: values.BenCellphone,
              BenEmail: values.BenEmail,
              BenRelationship: values.BenRelationship,
              BenEmployerName: values.BenEmployerName,
              MainAddress: values.MainAddress,
            },
            Addons: addons,
            PolicyUserID: values.PolicyUserID,
          },
          currentUser.id
        );

        if (response?.success) {
          setLoading(false);
          setProductInfo({});
          return true;
        }
        setErrorMsg(response?.message || "An error occurred while saving.");
        setLoading(false);
        return false;
      } else {
        const response = await handleUpdate(
          member.id,
          {
            Gender: values.Gender,
            Identity: values.Identity.value,
            IdNumber: values.IdNumber,
            DateOfBirth: toMemberApiDate(values.DateOfBirth),
            Title: values.Title,
            Initials: values.Initials,
            Surname: values.Surname,
            Name: values.Name,
            TelNumber: values.TelNumber,
            Cellphone: values.Cellphone,
            Email: values.Email,
            ExtendedAge: extendedAge,
            InceptionDate: toMemberApiInceptionDate(values.InceptionDate),
            // Group: values.Group.value ? values.Group.value : values.Group,
            PolicyNumber: values.PolicyNumber && values.PolicyNumber,
            Product: values.Product.value
              ? values.Product.value
              : values.Product,
            Premium: values.Premium ? values.Premium : "",
            SourceOfFunds: values.SourceOfFunds.value
              ? values.SourceOfFunds.value
              : values.SourceOfFunds,
            OtherSourceOfFunds: values.OtherSourceOfFunds,
            AddressLine1: values.AddressLine1,
            AddressLine2: values.AddressLine2,
            Suburb: values.Suburb,
            CityTown: values.CityTown,
            Province: values.Province,
            PostalCode: values.PostalCode,
            CoveredLive: formattedCardData,
            Beneficiary: {
              BenIdNumber: values.BenIdNumber,
              BenDateOfBirth: toMemberApiDate(values.BenDateOfBirth),
              BenTitle: values.BenTitle,
              BenInitials: values.BenInitials,
              BenSurname: values.BenSurname,
              BenName: values.BenName,
              BenGender: values.BenGender,
              BenTelNumber: values.BenTelNumber,
              BenCellphone: values.BenCellphone,
              BenEmail: values.BenEmail,
              BenRelationship: values.BenRelationship,
              BenEmployerName: values.BenEmployerName,
              MainAddress: values.MainAddress,
            },
            Addons: addons,
            PolicyUserID: values.PolicyUserID,
          },
          currentUser.id
        );

        if (response?.success) {
          setLoading(false);
          setProductInfo({});
          return true;
        }
        setErrorMsg(response?.message || "An error occurred while saving.");
        setLoading(false);
        return false;
      }
    },
    total: 9,
  });

  const resetModal = () => {
    showModal();
    gotoStep(0);
    setCoveredLive({});
    form.resetFields();
  };

  const { requestClose, onValuesChange, markModalSaved } = useGuardedModalClose({
    id: "edit-member-modal",
    open: show,
    hasChanges: current > 0 && current < 7,
    onClose: resetModal,
  });

  const handleModal = () => {
    void requestClose();
  };

  const handleCompletedClose = () => {
    markModalSaved();
    resetModal();
  };

  const resolveCardIndex = (item: any) => {
    const originalIndex = cardData.findIndex(
      (x: any) =>
        x === item || (x.id != null && item.id != null && x.id === item.id)
    );
    return originalIndex >= 0 ? originalIndex : 0;
  };

  const handleAddCoveredLive = (data: any) => {
    const values = form.getFieldsValue();

    const newItem = {
      CoveredName: data.CoveredName,
      CoveredSurname: data.CoveredSurname,
      CoveredIdNumber: data.CoveredIdNumber,
      CoveredDateOfBirth: dayjs(data.CoveredDateOfBirth)
        .startOf("day")
        .format("YYYY-MM-DD"),
      CoveredType: data.CoveredType,
      ExtendedProduct:
        typeof coveredProductInfo === "number" ? coveredProductInfo : null,
    };

    // Reset specific fields
    form.setFieldsValue({
      ...values,
      CoveredName: "",
      CoveredSurname: "",
      CoveredIdNumber: "",
      CoveredDateOfBirth: "",
      CoveredType: "",
      ExtendedProduct: "",
    });

    setCoveredProductInfo(null);
    setCardData((prev: any) => [...prev, newItem]);
  };

  const handleEditCoveredLive = (data: any, index: any) => {
    setIsEditing(true);
    setCoveredLive({ ...data });
    setEditingIndex(index);
  };

  const handleRemoveCoveredLive = (indexToRemove: number) => {
    const updatedCardData = cardData.filter(
      (_: any, i: number) => i !== indexToRemove
    );
    setCardData(updatedCardData);
  };

  const handleSaveCoveredLive = (data: any) => {
    const values = form.getFieldsValue();
    const updatedCardData = [...cardData];
    updatedCardData[editingIndex] = {
      id: coveredLive.id,
      CoveredType: data.CoveredType,
      CoveredName: data.CoveredName,
      CoveredSurname: data.CoveredSurname,
      CoveredIdNumber: data.CoveredIdNumber,
      CoveredDateOfBirth: dayjs(data.CoveredDateOfBirth)
        .startOf("day")
        .format("YYYY-MM-DD"),
      ExtendedProduct:
        data.CoveredType === "Extended" ? coveredProductInfo : null,
    };
    form.setFieldsValue({
      ...values,
      CoveredName: "",
      CoveredSurname: "",
      CoveredIdNumber: "",
      CoveredDateOfBirth: "",
      CoveredType: "",
      ExtendedProduct: "",
    });
    setCardData(updatedCardData);
    setIsEditing(false);
    setCoveredLive({});
  };

  // Step Styling
  const layout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 16 },
    labelAlign: "right" as const,
  };

  const display: any = [
    <PersonalInfo
      form={form}
      handleAge={handleAge}
      gotoStep={gotoStep}
      current={current}
    />,
    <AdditionalInfo
      memberAge={memberAge}
      gotoStep={gotoStep}
      current={current}
      form={form}
    />,
    <ResidentialInfo gotoStep={gotoStep} current={current} form={form} />,

    <ProductInfo
      memberAge={memberAge}
      gotoStep={gotoStep}
      current={current}
      form={form}
    />,
    <>
      <Row gutter={16}>
        <Col span={14} style={{ borderRight: "1px solid #f0f0f0", paddingRight: 16 }}>
          <CoveredLives
            gotoStep={gotoStep}
            current={current}
            form={form}
            handleAge={handleAge}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
            {isEditing ? (
              <Button
                type="primary"
                size="large"
                style={{ width: "35%", alignSelf: "center" }}
                onClick={() => {
                  const formData = form.getFieldsValue();
                  handleSaveCoveredLive(formData);
                }}
              >
                Save
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                style={{ width: "35%", alignSelf: "center" }}
                onClick={() => {
                  const formData = form.getFieldsValue();
                  handleAddCoveredLive(formData);
                }}
              >
                Add <RightOutlined />
              </Button>
            )}
            <Divider style={{ margin: "4px 0" }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                size="large"
                style={{ width: "50%" }}
                onClick={() => gotoStep(current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Col>
        <Col span={10}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flexWrap: "wrap",
              alignContent: "flex-start",
              maxHeight: 540,
              gap: 4,
              overflowX: "auto",
              padding: "2px 0",
            }}
          >
            {[...cardData]
              .sort((a: any, b: any) => {
                const order: Record<string, number> = {
                  Main: 0,
                  Spouse: 1,
                  Dependant: 2,
                  Extended: 3,
                };
                return (order[a.CoveredType] ?? 99) - (order[b.CoveredType] ?? 99);
              })
              .map((item: any) => {
                const typeColorMap: Record<
                  string,
                  { bg: string; border: string; label: string }
                > = {
                  Main: { bg: "#e6f4ff", border: "#91caff", label: "#0958d9" },
                  Extended: {
                    bg: "#f6ffed",
                    border: "#b7eb8f",
                    label: "#389e0d",
                  },
                  Spouse: {
                    bg: "#f9f0ff",
                    border: "#d3adf7",
                    label: "#531dab",
                  },
                  Dependant: {
                    bg: "#fff7e6",
                    border: "#ffd591",
                    label: "#d46b08",
                  },
                };
                const colors = typeColorMap[item.CoveredType] || {
                  bg: "#fafafa",
                  border: "#d9d9d9",
                  label: "#595959",
                };
                const originalIndex = resolveCardIndex(item);
                return (
                  <div
                    key={item.id ?? originalIndex}
                    style={{
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.bg,
                      borderRadius: 6,
                      padding: "4px 8px",
                      width: 180,
                      height: 52,
                      boxSizing: "border-box" as const,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 4,
                        }}
                      >
                        <UserOutlined
                          style={{
                            fontSize: 10,
                            color: colors.label,
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#262626",
                            lineHeight: "16px",
                            wordBreak: "break-word",
                          }}
                        >
                          {item.CoveredName} {item.CoveredSurname}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: colors.label,
                          paddingLeft: 14,
                          marginTop: 1,
                        }}
                      >
                        {item.CoveredType}
                      </div>
                    </div>
                    <Space size={4} style={{ flexShrink: 0, marginTop: 2 }}>
                      <EditOutlined
                        style={{
                          color: "#389e0d",
                          fontSize: 11,
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleEditCoveredLive(item, originalIndex)
                        }
                      />
                      <DeleteOutlined
                        style={{
                          color: "#cf1322",
                          fontSize: 11,
                          cursor: "pointer",
                        }}
                        onClick={() => handleRemoveCoveredLive(originalIndex)}
                      />
                    </Space>
                  </div>
                );
              })}
          </div>
        </Col>
      </Row>
    </>,
    <Beneficiary
      gotoStep={gotoStep}
      current={current}
      handleAge={handleAge}
      form={form}
      coveredLives={cardData}
    />,
    <Addon
      loading={loading}
      gotoStep={gotoStep}
      current={current}
      submit={submit}
      form={form}
      actionLabel="Next"
      failStep={8}
    />,
  ];

  return (
    <Modal
      title="Edit Member"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={1200}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 7 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Personal Info" />
              <Step title="Additional Info" />
              <Step title="Residential Info" />
              <Step title="Product Info" />
              <Step title="Covered Lives" />
              <Step title="Beneficiary" />
              <Step title="Addons" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, marginTop: 24 }}>
          <Form
            initialValues={coveredLive}
            {...layout}
            {...formProps}
            style={{ maxWidth: 1100 }}
            onValuesChange={onValuesChange}
          >
            {display[current]}
          </Form>

          {current === 7 && (
            <Result status="success" title="Successfully updated.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 8 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditMemberModal;
